<template>
  <DialogHeader>
    <DialogTitle>Cancellation Request</DialogTitle>
    <DialogDescription>
      Shopper is disputing the cancellation terms.
      <br />
      Please review, update, or confirm as-is.
    </DialogDescription>
  </DialogHeader>
  <SectionDivider />
  <h3 class="text-grey-light-900 font-semibold">Cancellation Reason</h3>
  <div class="flex flex-col">
    <TextBoxInput :label="lastNotification?.reason" :model-value="cancellationMessage" disabled />
    <p class="text-grey-light-600 text-xs mt-1">
      {{ displayDateTime(lastNotification?.date) }} | {{ props.order?.name }}
    </p>
  </div>
  <div>
    <SectionDivider />
    <h3 class="flex justify-between py-4 text-grey-light-900">
      <span class="font-semibold">Cancellation Fee</span>
      <span class="font-semibold">{{ displayCurrency(cancellationFee) }}</span>
    </h3>

    <SectionDivider />
  </div>
  <h3 class="text-grey-light-900 font-semibold">Cancellation Amendments</h3>
  <div class="grid grid-cols-1 gap-4">
    <div class="col-span-1">
      <SelectInput
        label="Propose new cancellation terms"
        :hide-label="false"
        :items="['Deposit', 'Flat Fee', 'Percentage', 'No Fee']"
        v-model="cancellationType"
        data-testid="cancellationType"
      />
    </div>
    <div v-if="cancellationType === 'Percentage'" class="col-span-1">
      <NumberInput v-model="percentage" v-model:is-valid="isAmountValid" :min="0" :max="100" />
    </div>
    <div
      v-if="cancellationType === 'Flat Fee' || cancellationType === 'Deposit' || cancellationType === 'Percentage'"
      class="col-span-1"
    >
      <CurrencyInput
        :label="`New Cancellation Fee`"
        :disabled="isLoading || cancellationType === 'Deposit' || cancellationType === 'Percentage'"
        v-model="amount"
        v-model:is-valid="isAmountValid"
      />
    </div>
    <div class="col-span-1">
      <TextBoxInput :label="`Message*`" v-model="message" v-model:is-input-valid="isMessageValid" />
    </div>
  </div>

  <DialogFooter>
    <Button type="button" @click="onSend" :disabled="!shouldEnableSend">
      <LayupSpinner v-if="isLoading" :size="4" />
      <span>Send</span>
    </Button>
  </DialogFooter>
  <p v-if="errorMessage" class="text-sm font-medium text-error-500 text-center">
    {{ errorMessage }}
  </p>
</template>

<script setup lang="ts">
import CurrencyInput from '@/components/common/CurrencyInput.vue';
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import NumberInput from '@/components/common/NumberInput.vue';
import SectionDivider from '@/components/common/SectionDivider.vue';
import SelectInput from '@/components/common/SelectInput_v2.vue';
import TextBoxInput from '@/components/common/TextBoxInput.vue';
import { Button } from '@/components/ui/button';
import { DialogHeader, DialogTitle } from '@/components/ui/dialog';
import DialogDescription from '@/components/ui/dialog/DialogDescription.vue';
import DialogFooter from '@/components/ui/dialog/DialogFooter.vue';
import { NetworkError } from '@/errors';
import { useUserStore } from '@/stores/users';
import { displayCurrency, displayDateTime, toCents } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { calculateCancellationFee } from '@/utils/order-cancellation-terms';
import { sendOrderNotification } from '@/utils/order-notifications.utils';
import { AxiosError } from 'axios';
import { computed, ref, watch } from 'vue';
import type { ModelOrderResponse } from '@layup/api-sdk';

const userStore = useUserStore();

const props = defineProps<{
  order: ModelOrderResponse;
}>();

const emit = defineEmits(['next']);

const getDeposit = (order: ModelOrderResponse) => {
  return order.depositAmount ? order.depositAmount / 100 : 0;
};

const isLoading = ref<boolean>(false);
// TODO: This should be the last cancellation message not the last message.
const lastNotification = props.order?.notifications?.slice(-1)[0];
const cancellationMessage = ref<string>(lastNotification?.details || '');
const cancellationType = ref<string>('Deposit');
const amount = ref<number>(getDeposit(props.order));
const percentage = ref<number>(0);
const isAmountValid = ref<boolean>(false);
const message = ref<string>('');
const isMessageValid = ref<boolean>(false);
const errorMessage = ref<string>('');
const orderAmountWithFees = props.order.plans!.reduce((s, p: any) => s + p.amountDue + p.depositDue, 0);
const cancellationFee = calculateCancellationFee(
  props.order.cancellationTerms!,
  props.order.depositAmount || 0,
  orderAmountWithFees,
);

const shouldEnableSend = computed(() => {
  return (isAmountValid.value || cancellationType.value === 'No Fee') && isMessageValid.value;
});

watch(cancellationType, (newCancellationType) => {
  if (newCancellationType === 'Deposit') {
    amount.value = getDeposit(props.order);
  } else {
    amount.value = 0;
    percentage.value = 0;
  }
});

watch(percentage, (newPercentage) => {
  amount.value = Math.round(orderAmountWithFees * (newPercentage / 100)) / 100;
});

const onSend = async () => {
  try {
    isLoading.value = true;
    await sendOrderNotification(
      props.order,
      cancellationType.value,
      message.value,
      {
        id: userStore.user._id!.toString(),
        name: userStore.user.name,
      },
      percentage.value,
      toCents(amount.value),
    );
    addMetric('order-dispute-response', {
      success: true,
    });
    emit('next');
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('order-dispute-response', {
        success: false,
        error: (error as Error).message,
      });
      errorMessage.value = 'Network issue. Please refresh.';
    }
    createException(error);
    addMetric('order-dispute-response', {
      success: false,
      error: (error as Error).message,
    });
    if (error instanceof AxiosError) {
      if (error.response?.status === 400) {
        errorMessage.value = error.response?.data || 'Something was wrong with your request. Please contact support.';
      }
    } else {
      errorMessage.value = 'Request failed. Please contact support.';
    }
  } finally {
    isLoading.value = false;
  }
};
</script>
